/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { TicketButton } from '../../shared/ticket/TicketButton';

type CatalogProductFormActionsProps = {
  isDraftReady: boolean;
  saving: boolean;
  imageUploading?: boolean;
  primaryActionLabel: string;
  draftValidationMessage: string | null;
  onClose: () => void;
  onSave: () => void;
  onSaveAndContinue?: () => void;
  showSaveAndContinue?: boolean;
  onSaveAttemptWhenInvalid?: (message: string) => void;
  compactMode?: boolean;
  onExpandForm?: () => void;
  mobile?: boolean;
};

export function CatalogProductFormActions({
  isDraftReady,
  saving,
  imageUploading = false,
  primaryActionLabel,
  draftValidationMessage,
  onClose,
  onSave,
  onSaveAndContinue,
  showSaveAndContinue = false,
  onSaveAttemptWhenInvalid,
  compactMode = false,
  onExpandForm,
  mobile = false,
}: CatalogProductFormActionsProps) {
  const busy = saving || imageUploading;

  const trySave = () => {
    if (busy) return;
    if (!isDraftReady && draftValidationMessage && onSaveAttemptWhenInvalid) {
      onSaveAttemptWhenInvalid(draftValidationMessage);
      return;
    }
    onSave();
  };

  const trySaveAndContinue = () => {
    if (busy) return;
    if (!isDraftReady && draftValidationMessage && onSaveAttemptWhenInvalid) {
      onSaveAttemptWhenInvalid(draftValidationMessage);
      return;
    }
    onSaveAndContinue?.();
  };

  const quickContinue = compactMode && !mobile && showSaveAndContinue && onSaveAndContinue;

  return (
    <View style={mobile ? styles.formActionsDockMobile : styles.formActionsDock}>
      <View style={[styles.formActionsRow, compactMode && styles.formActionsRowQuick]}>
        {!mobile ? (
          <View style={styles.formActionsMeta}>
            {compactMode && onExpandForm ? (
              <TouchableOpacity
                onPress={onExpandForm}
                accessibilityRole="button"
                accessibilityLabel="Ver más opciones del producto"
                testID="catalog.form.expand"
              >
                <Text style={styles.formActionsExpand}>Más opciones →</Text>
              </TouchableOpacity>
            ) : null}
            {(compactMode || showSaveAndContinue) ? (
              <Text style={styles.formActionsHint}>Con nombre y precio alcanza.</Text>
            ) : null}
          </View>
        ) : null}
        <TicketButton
          label="Cancelar"
          onPress={onClose}
          variant="ghost"
          disabled={busy}
          style={[mobile ? styles.formActionButtonMobile : styles.formActionButtonDesktop, busy && styles.disabled]}
        />
        {quickContinue ? (
          <>
            <TicketButton
              label={primaryActionLabel}
              onPress={trySave}
              variant="ghost"
              disabled={busy}
              style={[styles.formActionButtonDesktop, (!isDraftReady || busy) && styles.disabled]}
              accessibilityLabel={primaryActionLabel}
              testID="catalog.form.submit"
            />
            <TicketButton
              label={saving ? 'Guardando…' : 'Guardar y agregar otro'}
              onPress={trySaveAndContinue}
              variant="gold"
              disabled={busy}
              style={[styles.formActionButtonDesktop, (!isDraftReady || busy) && styles.disabled]}
              accessibilityLabel="Guardar y agregar otro"
              testID="catalog.form.save-next"
            />
          </>
        ) : (
          <>
            {showSaveAndContinue && onSaveAndContinue && !mobile ? (
              <TicketButton
                label={saving ? 'Guardando…' : 'Guardar y agregar otro'}
                onPress={trySaveAndContinue}
                variant="ghost"
                disabled={busy}
                style={[styles.formActionButtonDesktop, (!isDraftReady || busy) && styles.disabled]}
                accessibilityLabel="Guardar y agregar otro"
              />
            ) : null}
            <TicketButton
              label={saving ? 'Guardando…' : primaryActionLabel}
              onPress={trySave}
              variant="gold"
              disabled={busy}
              style={[mobile ? styles.formActionButtonMobile : styles.formActionButtonDesktop, (!isDraftReady || busy) && styles.disabled]}
              accessibilityLabel={primaryActionLabel}
              testID="catalog.form.submit"
            />
          </>
        )}
      </View>
      {/* En mobile el tercer botón wrappea a 2 líneas — va como link debajo. */}
      {showSaveAndContinue && onSaveAndContinue && mobile ? (
        <TouchableOpacity
          onPress={trySaveAndContinue}
          disabled={!isDraftReady || busy}
          style={[styles.saveAndContinueLink, (!isDraftReady || busy) && styles.disabled]}
          accessibilityRole="button"
          accessibilityLabel="Guardar y seguir cargando"
        >
          <Text style={styles.saveAndContinueLinkText}>
            {saving ? 'Guardando…' : 'Guardar y seguir cargando'}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  formActionsDock: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 22,
    paddingTop: 14,
    paddingBottom: 14,
  },
  formActionsDockMobile: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: Math.max(20, spacing.md),
  },
  formActionsRow: {
    width: '100%',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  formActionsRowQuick: {
    gap: 10,
  },
  formActionsMeta: {
    flex: 1,
    minWidth: 120,
    gap: spacing.sm,
  },
  formActionsExpand: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.brandPrimary,
  },
  formActionsHint: {
    fontSize: 12,
    lineHeight: 16,
    color: ticketColors.ink3,
  },
  formActionButtonDesktop: {
    flexShrink: 0,
  },
  formActionButtonMobile: {
    flex: 1,
  },
  saveAndContinueLink: {
    alignSelf: 'center',
    marginTop: 10,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  saveAndContinueLinkText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  disabled: {
    opacity: 0.45,
  },
});
